/**
 * Se a pessoa pode alguma coisa -- a pergunta que menu, rota e botao fazem.
 *
 * O nucleo e puro de proposito: quem le o localStorage e `codigosDoUsuario`, e
 * so ela. Assim a regra, que e a parte que erra, da para testar sem navegador.
 *
 * O codigo tem a forma `<ferramenta>:<acao>` -- `accounts.grupos:editar`. Vem
 * pronto do `GET /api/user/{id}/permission`, na chave `codes`, montado pelo
 * PermService a partir de `feature.code` e do nome da acao.
 */

const CHAVE = 'permissionsApi';

/**
 * Sinal a parte, fora de `permissionsApi`: "o cache atual pode ser de uma
 * identidade que ja nao e a de agora, nao confie nele".
 *
 * Existe porque `codigos`/`registrados` sozinhos nao distinguem dois estados
 * que precisam de tratamento OPOSTO:
 *
 *   - catalogo genuinamente vazio (ambiente novo, ninguem cadastrou nada
 *     ainda) -- `pode()` deve LIBERAR, e' o que faz o grupo admin conseguir
 *     entrar para criar o catalogo;
 *   - a troca de identidade (personificar, despersonificar, trocar de
 *     empresa) pediu a permissao de novo, a busca FALHOU, e o que sobrou no
 *     localStorage e' o cache da identidade ANTERIOR -- `pode()` deve NEGAR,
 *     senao um administrador que acabou de personificar alguem sem acesso
 *     nenhum continua liberado com base em quem ele NAO e' mais.
 *
 * Pelo formato dos dados os dois casos sao identicos (`codes: []`,
 * `codes_registrados: []` ou ausentes). A diferenca so existe em quem SABE
 * que uma busca falhou -- por isso o sinal e' escrito explicitamente por
 * quem tenta a busca (`personificacao.js`), nao inferido daqui.
 */
const CHAVE_DESCONFIANCA = 'permissoesDesconfiaveis';

/**
 * Marca o cache de permissao atual como nao confiavel.
 *
 * Chamar depois de esgotar as tentativas de rebuscar a permissao apos trocar
 * de identidade -- ver `atualizarPermissoes()` em `personificacao.js`.
 */
export function marcarPermissaoDesconfiavel() {
    try {
        localStorage.setItem(CHAVE_DESCONFIANCA, '1');
    } catch {
        // localStorage indisponivel: nada a fazer, o pior caso e' o de antes
        // deste sinal existir (fail-open de `pode()`).
    }
}

/** Volta a confiar no cache -- chamado depois que uma busca de verdade tem sucesso. */
export function confiarNaPermissaoDeNovo() {
    try {
        localStorage.removeItem(CHAVE_DESCONFIANCA);
    } catch {
        // Ver marcarPermissaoDesconfiavel().
    }
}

export function permissaoEhDesconfiavel() {
    try {
        return localStorage.getItem(CHAVE_DESCONFIANCA) === '1';
    } catch {
        return false;
    }
}

/**
 * A REGRA DE FALHA, e ela e deliberada: na duvida, LIBERA.
 *
 * `codigos` desconhecido -- nao carregou, deu erro, versao antiga da API sem a
 * chave `codes` -- devolve `true`. Tres razoes:
 *
 *  1. A API confere de verdade. `AuthService::isAuthorized()` monta o caminho
 *     a partir do host e da rota e confere contra a lista do usuario; quem nao
 *     pode toma 403 no endpoint. A tela e conveniencia, nao barreira.
 *  2. Falhar fechado aqui ja trancou a aplicacao uma vez: catalogo vazio ->
 *     arvore vazia -> ninguem e admin -> 403 em tudo -> laco de volta ao admin.
 *  3. Uma tela a mais visivel e incomodo. O app inteiro inacessivel e
 *     incidente.
 *
 * Lista vazia e diferente de desconhecida: `[]` quer dizer "carregou e a pessoa
 * nao tem nada", e ai fecha.
 *
 * A EXCECAO e' `confiavel = false`: dispara antes de tudo porque, ao contrario
 * de "desconhecido", "desconfiavel" nao e' du'vida -- e' cache que se sabe
 * ser de outra identidade (ver `permissaoEhDesconfiavel()`). Fail-open nao se
 * aplica. E' PARAMETRO, e nao uma leitura de localStorage aqui dentro, para
 * `pode()` continuar puro e testavel sem navegador -- quem le o sinal e quem
 * ja lia `codigos`/`registrados` (`podeAgora()`, o guard de rotas, o
 * `LeftSidebar`).
 */
export function pode(codigo, codigos, registrados = null, confiavel = true) {
    if (!codigo) {
        return true;
    }

    if (!confiavel) {
        return false;
    }

    if (!Array.isArray(codigos)) {
        return true;
    }

    if (codigos.includes('*')) {
        return true;
    }

    // Permissao que AINDA NAO FOI CADASTRADA nao barra ninguem.
    //
    // `registrados` e o catalogo do ambiente, nao a lista da pessoa. Sem essa
    // distincao, cadastrar a primeira permissao trancaria todas as telas de
    // uma vez: as rotas daqui ja declaram catorze codigos, e no instante em que
    // `codes` comeca a ser enviada os treze ainda nao cadastrados viram
    // negativa -- nao porque a pessoa nao pode, mas porque o codigo nao existe.
    //
    // Com o catalogo, cada permissao passa a valer quando e cadastrada. O
    // caminho deixa de ser um big-bang.
    if (Array.isArray(registrados) && !registrados.includes(codigo)) {
        return true;
    }

    return codigos.includes(codigo);
}

/** Verdadeiro se puder qualquer um da lista. Para no com varios filhos. */
export function podeAlgum(exigidos, codigos, registrados = null, confiavel = true) {
    if (!Array.isArray(exigidos) || exigidos.length === 0) {
        return true;
    }

    return exigidos.some((codigo) => pode(codigo, codigos, registrados, confiavel));
}

/**
 * Os codigos do usuario, ou `null` quando nao se sabe.
 *
 * `null` e o valor que faz o `pode` liberar. Devolver `[]` aqui num erro de
 * leitura trancaria a aplicacao inteira -- por isso a distincao e explicita e
 * o catch devolve null, nao array vazio.
 */
export function codigosDoUsuario() {
    try {
        const bruto = localStorage.getItem(CHAVE);
        if (!bruto) {
            return null;
        }

        const dados = JSON.parse(bruto);
        return Array.isArray(dados?.codes) ? dados.codes : null;
    } catch {
        return null;
    }
}

/**
 * Os codigos que EXISTEM no ambiente, ou `null` quando nao se sabe.
 *
 * Catalogo, nao permissao: diz o que ja foi cadastrado, para que uma permissao
 * ainda inexistente nao barre ninguem. `null` faz o `pode` conferir so contra
 * a lista da pessoa, que e o comportamento de antes.
 */
export function codigosRegistrados() {
    try {
        const bruto = localStorage.getItem(CHAVE);
        if (!bruto) {
            return null;
        }

        const dados = JSON.parse(bruto);
        return Array.isArray(dados?.codes_registrados) ? dados.codes_registrados : null;
    } catch {
        return null;
    }
}

/** Atalho para as telas: `podeAgora('accounts.grupos:editar')`. */
export function podeAgora(codigo) {
    return pode(codigo, codigosDoUsuario(), codigosRegistrados(), !permissaoEhDesconfiavel());
}
